
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

interface PaymentProcessingProps {
  orderId: string;
  amount: number;
  paymentMethod: string;
  onPaymentComplete: (paymentData: any) => void;
  onCancel: () => void;
}

export const PaymentProcessing: React.FC<PaymentProcessingProps> = ({
  orderId,
  amount,
  paymentMethod,
  onPaymentComplete,
  onCancel
}) => {
  const [selectedMethod, setSelectedMethod] = useState('cash');
  const { register, handleSubmit, formState: { errors } } = useForm();

  const cashLimit = 200000; // ₹2,00,000 cash limit

  const onSubmit = async (data: any) => {
    const paymentData = {
      order_id: orderId,
      amount: amount,
      payment_method: selectedMethod,
      payment_date: new Date().toISOString(),
      reference_number: selectedMethod === 'cash' ? 
        `CASH_${Date.now()}` : 
        data.reference_number,
      bank_details: selectedMethod !== 'cash' ? {
        bank_name: data.bank_name,
        account_number: data.account_number,
        ifsc_code: data.ifsc_code
      } : null,
      card_details: selectedMethod === 'card' ? {
        last_four_digits: data.card_number?.slice(-4),
        card_type: data.card_type
      } : null,
      status: 'completed'
    };

    onPaymentComplete(paymentData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-primary">Payment Processing</h2>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Payment Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-3">Payment Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Order ID:</span>
                <span className="font-medium">{orderId}</span>
              </div>
              <div className="flex justify-between">
                <span>Payment Type:</span>
                <span className="font-medium capitalize">{paymentMethod.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Amount to Pay:</span>
                <span>₹{amount.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Payment Method Selection */}
            <div className="space-y-4">
              <h3 className="font-semibold">Select Payment Method</h3>
              
              {/* Cash Payment */}
              <label className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                amount > cashLimit ? 'opacity-50 cursor-not-allowed' : ''
              }`}>
                <input
                  type="radio"
                  value="cash"
                  checked={selectedMethod === 'cash'}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  disabled={amount > cashLimit}
                  className="text-primary"
                />
                <div className="flex-1">
                  <div className="font-medium flex items-center">
                    <i className="ri-money-rupee-circle-line mr-2 text-green-600"></i>
                    Cash Payment
                  </div>
                  <div className="text-sm text-gray-600">
                    {amount > cashLimit ? 
                      `Amount exceeds cash limit of ₹${cashLimit.toLocaleString()}` :
                      `Available for amounts up to ₹${cashLimit.toLocaleString()}`
                    }
                  </div>
                </div>
              </label>

              {/* Card Payment */}
              <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="card"
                  checked={selectedMethod === 'card'}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  className="text-primary"
                />
                <div className="flex-1">
                  <div className="font-medium flex items-center">
                    <i className="ri-bank-card-line mr-2 text-blue-600"></i>
                    Card Payment
                  </div>
                  <div className="text-sm text-gray-600">Credit/Debit card payment</div>
                </div>
              </label>

              {/* Bank Transfer */}
              <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="bank_transfer"
                  checked={selectedMethod === 'bank_transfer'}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  className="text-primary"
                />
                <div className="flex-1">
                  <div className="font-medium flex items-center">
                    <i className="ri-bank-line mr-2 text-purple-600"></i>
                    Bank Transfer (RTGS/NEFT)
                  </div>
                  <div className="text-sm text-gray-600">Direct bank transfer</div>
                </div>
              </label>
            </div>

            {/* Payment Details Forms */}
            {selectedMethod === 'card' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900">Card Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Card Number</label>
                    <input
                      {...register('card_number', { required: 'Card number is required' })}
                      type="text"
                      placeholder="**** **** **** 1234"
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {errors.card_number && (
                      <p className="text-red-500 text-xs mt-1">{errors.card_number.message as string}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Card Type</label>
                    <select
                      {...register('card_type', { required: 'Card type is required' })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select Card Type</option>
                      <option value="visa">Visa</option>
                      <option value="mastercard">Mastercard</option>
                      <option value="rupay">RuPay</option>
                      <option value="amex">American Express</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {selectedMethod === 'bank_transfer' && (
              <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium text-purple-900">Bank Transfer Details</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Reference Number</label>
                    <input
                      {...register('reference_number', { required: 'Reference number is required' })}
                      type="text"
                      placeholder="Enter transaction reference number"
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {errors.reference_number && (
                      <p className="text-red-500 text-xs mt-1">{errors.reference_number.message as string}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Bank Name</label>
                      <input
                        {...register('bank_name', { required: 'Bank name is required' })}
                        type="text"
                        placeholder="Enter bank name"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">IFSC Code</label>
                      <input
                        {...register('ifsc_code', { required: 'IFSC code is required' })}
                        type="text"
                        placeholder="Enter IFSC code"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedMethod === 'cash' && (
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center text-green-800">
                  <i className="ri-information-line mr-2"></i>
                  <span className="font-medium">Cash Payment Confirmation</span>
                </div>
                <p className="text-green-700 text-sm mt-1">
                  Please confirm that you have received ₹{amount.toLocaleString()} in cash from the customer.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
              >
                <i className="ri-check-line mr-2"></i>
                Confirm Payment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessing;
